<template>
  <!-- Attach organization-specific CSS variables to the entire app, so
       all components can access them -->
  <v-app>
    <div
      id="app"
      ref="appComponent"
      :style="organizationCssVars"
      :class="{
        'employee-user': !user || user.isEmployee,
        'admin-user': user && user.isAdmin,
        'staff-user': user && user.isStaff,
      }"
    >
      <Spinner v-if="loading" />
      <div v-else class="fixed-width">
        <Header />
        <alert-alert />
        <Modal
          v-if="organizationMessage.isOpen"
          dimmed
          isOrgMessage
          class-name="organization-message"
          :width="80"
          @close-modal="organizationMessage.isOpen = false"
        >
          <div class="organization-message">
            <div class="header">
              <font-awesome-icon
                class="close-message"
                :icon="['fa', 'times']"
                :style="{ fontSize: '24px' }"
                @click="closeMessageModal"
              ></font-awesome-icon>
              <font-awesome-icon
                class="lightbulb"
                :icon="['fa', 'lightbulb']"
                :style="{ fontSize: '48px' }"
              ></font-awesome-icon>
            </div>
            <div class="message">
              <h2>{{ organizationDetails.organizationMessage.title }}</h2>
              <div v-html="organizationDetails.organizationMessage.content"></div>
            </div>
          </div>
        </Modal>

        <Modal
          v-if="apiError"
          dimmed
          isOrgMessage
          class-name="apiError"
          :width="80"
          @close-modal="apiError = false"
        >
          <div class="organization-message">
            <div class="header">
              <font-awesome-icon
                class="close-message"
                :icon="['fa', 'times']"
                :style="{ fontSize: '24px' }"
                @click="apiError = false"
              ></font-awesome-icon>
              <font-awesome-icon
                class="lightbulb"
                :icon="['fas', 'exclamation']"
                :style="{ fontSize: '48px' }"
              ></font-awesome-icon>
            </div>
            <div class="message">
              <h2>There is an issue</h2>
              <div>
                It looks like there is an error preventing us from connecting. Please try again.
              </div>

              <md-button @click="refresh" class="md-dense md-raised primary-btn">
                <span class="inherit-font" v-translate>Refresh</span>
              </md-button>
            </div>
          </div>
        </Modal>

        <!-- Binding a key to the full path will remount a view if the detail endpoint changes-->
        <div class="view-content">
          <router-view :key="$route.fullPath"></router-view>
        </div>

        <Footer />
      </div>
    </div>
  </v-app>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import VueScrollTo from 'vue-scrollto'
import Spinner from '@/components/Spinner'
import Modal from '@/components/Modal'
import Organization from './services/organizations'
import Header from './views/Header'
import Footer from './views/Footer'
import store from '@/store'
import { initStripe } from '@/services/stripe'
import auth from '@/services/auth'
import { getTranslations } from '@/services/translations'

export default {
  name: 'app',

  metaInfo() {
    if (window.location.hostname.includes('staging')) {
      return {
        meta: [{ name: 'robots', content: 'noindex, nofollow, noarchive' }],
      }
    }
  },
  components: {
    Header,
    Spinner,
    Footer,
    Modal,
  },
  data() {
    return {
      loading: null,
      organization: null,
      apiError: false,
      organizationMessage: {
        isOpen: false,
      },
    }
  },
  methods: {
    setPageTitle(organization) {
      document.title = organization.canaryBrandTitle
    },
    closeMessageModal() {
      this.organizationMessage.isOpen = false
    },
    async refresh() {
      if (this.apiError) {
        this.apiError = false
      }

      this.loading = true
      await Organization.api
        .getMyOrganization()
        .then(async organization => {
          this.$store.dispatch('updateOrganizationDetails', organization)
        })
        .finally(() => {
          this.loading = false
        })

      // refresh language
      const translations = await getTranslations()
      if (Object.keys(translations).length) {
        this.$translate.initTranslations(translations, this.$translate.config)
      }

      // Always fetch fresh user profile data
      await this.$store.dispatch('refreshCurrentUser')

      // Check whether to force logout due to bad app state.
      const loggedOut = await this.$store.dispatch('checkShouldLogOut', { route: this.$route })

      if (
        this.organizationDetails.organizationMessage &&
        this.organizationDetails.organizationMessage.isActive
      ) {
        this.organizationMessage.isOpen = true
      }

      if (loggedOut) {
        this.$router.replace({
          name: 'Login',
          query: { next: this.$route.path },
        })
      }
    },
  },
  async beforeMount() {
    await this.refresh()
  },
  /**
   * If user is not logged in, clear out state, to prevent caching bad data.
   */
  beforeDestroy() {
    if (!this.userIsLoggedIn) {
      store.commit('RESET_STATE')
    }
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    ...mapGetters(['organizationCssVars', 'userIsLoggedIn']),
  },
  watch: {
    organizationDetails: async function updateInitStripe() {
      const user = await this.user
      this.organizationDetails.stripeCredentials &&
        this.organizationDetails.stripeCredentials.publishableKey &&
        initStripe(
          this.organizationDetails.stripeCredentials.publishableKey,
          (user && user?.preferredLanguage) || this.$language.current || 'en',
        )

      this.setPageTitle(this.organizationDetails)
    },
    // When route changes, scroll to the top
    '$route.path': function watchRoutePath() {
      VueScrollTo.scrollTo('#app', 200)
    },
    '$language.current': function changeStripeLanguage() {
      auth.getAndSetOrganization()
      if (this.organizationDetails?.stripeCredentials?.publishableKey) {
        initStripe(
          this.organizationDetails.stripeCredentials.publishableKey,
          (this.user && this.user?.preferredLanguage) || this.$language.current || 'en',
        )
      }
    },
  },
}
</script>

<style lang="scss">
@import './styles/base.scss';

// Elan(10/1/21) --------- Default Vue Material Themes ---------
@import '~vue-material/dist/theme/engine';
@include md-register-theme('default', ());
@import '~vue-material/dist/components/MdDatepicker/theme';
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/responsive';

// ------ Fixed Width ------
.fixed-width {
  background-color: white;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  /* Desktop max-width */
  @include desktop-screen {
    max-width: $desktop-max-width;
  }
}

.view-content {
  flex: 10;
}

#app {
  min-height: 100vh;
  background-color: #e6ecef;
}

// Material Design Overrides
.md-button[disabled] {
  background-color: $mid-gray;
  border-color: $mid-gray;
}
.md-button .md-button-content {
  font-family: $base-content-font-family;
}
.md-primary {
  background-color: $blue;
  color: white;
}
.md-button.md-primary[disabled] {
  background-color: $mid-gray;
}

.md-checkbox {
  .md-checkbox-container {
    border-color: rgba(0, 0, 0, 0.54);
  }

  .md-checkbox-label {
    height: auto;
  }

  &.md-checked .md-checkbox-container {
    background-color: $mid-gray;
    border-color: $mid-gray;

    &::after {
      border-color: $white;
    }
  }

  &.md-primary {
    background-color: transparent;
    color: white;
  }
}

.md-field {
  .md-input {
    border-bottom: 1px solid $mid-gray;
  }

  &.md-has-textarea {
    border: 1px solid $mid-gray;
    border-radius: 0.5rem;
    background-color: $off-white;
  }
}

.md-menu.md-select {
  border-bottom: 1px solid $very-light-gray;
}
.md-select .md-input {
  border-bottom: none;
}

// Set select boxes to not have a minimum height - this means all options will show
.md-menu-content {
  background-color: white;
}

.md-menu-content.md-menu-content-big {
  // min-height: 35vh;
}

.md-list {
  .md-list-item {
    margin-left: 0;
    padding: 0;
  }
  .md-list-item-content {
    min-height: unset;
  }
  .md-list-item-text {
    font-size: 1rem;
    white-space: normal;
  }
}

.organization-message {
  .header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: var(--dark-brand-color);
    height: 100px;
  }
  .close-message {
    cursor: pointer;
    position: absolute;
    top: 10px;
    right: 10px;
    color: $white;
    flex-grow: 0;
  }
  .message {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .lightbulb {
    color: $white;
  }
}

.inherit-font {
  font-weight: inherit;
  font-size: inherit;
}

.primary-btn {
  margin-top: 1.2rem;
  @include primary-button(var(--dark-brand-color));
  margin-bottom: 1.2rem;
}

.chat-bubble {
  background: red !important;
}
</style>
